import apiUtil from "@/util/apiUtil";
import {InsightCard} from "../insightCardModels";

const BASE_URL = "/api/ActionPlan";

export interface InsightActionJustificationRequest {
    insightTitle?: string;
    insightSummary?: string;
    aiNote?: string;
    headlineMetricValue?: string;
    headlineMetricLabel?: string;
    actionTitle: string;
    actionCategory?: string;
    actionDescription?: string;
    impactSummary?: string;
    dollarRoi?: string;
}

interface InsightActionJustificationResponse {
    justification: string;
}

const justificationPromiseCache = new Map<string, Promise<string>>();

function buildCacheKey(request: InsightActionJustificationRequest): string {
    return JSON.stringify(request);
}

function extractDollarRoi(card: InsightCard): string | undefined {
    const candidateTexts = [
        card.headlineMetric.value,
        card.headlineMetric.label,
        card.narrative,
        card.aiNote ?? ""
    ];

    for (const text of candidateTexts) {
        const match = text.match(/\$\s*[\d,.]+\s*[kKmM]?/);
        if (match) {
            return match[0].replace(/\s+/g, "").replace("$", "$ ").trim();
        }
    }

    return undefined;
}

export function buildJustificationRequest(
    card: InsightCard,
    actionTitle: string,
    actionCategory: string,
    actionDescription: string
): InsightActionJustificationRequest {
    const impactSummary = `${card.headlineMetric.value} — ${card.headlineMetric.label}`;

    return {
        insightTitle: card.title,
        insightSummary: card.narrative,
        aiNote: card.aiNote,
        headlineMetricValue: card.headlineMetric.value,
        headlineMetricLabel: card.headlineMetric.label,
        actionTitle,
        actionCategory,
        actionDescription,
        impactSummary,
        dollarRoi: extractDollarRoi(card)
    };
}

export function buildFallbackJustification(
    actionTitle: string,
    actionDescription: string,
    dollarRoi?: string
): string {
    const normalizedDescription = actionDescription.replace(/\s+/g, " ").trim();
    const withoutTerminalPunctuation = normalizedDescription.replace(/[.!?]+$/, "");
    const leadIn = `I recommend "${actionTitle}" first because ${withoutTerminalPunctuation
        .charAt(0)
        .toLowerCase()}${withoutTerminalPunctuation.slice(1)}`;

    if (dollarRoi) {
        return `${leadIn}, and it protects or unlocks roughly ${dollarRoi} in impact.`;
    }

    return `${leadIn}.`;
}

/**
 * Ask the backend LLM for a one-line recommendation rationale and cache it by request payload
 * so multiple insight surfaces do not trigger duplicate generation calls.
 */
export function generateInsightActionJustification(
    request: InsightActionJustificationRequest
): Promise<string> {
    const cacheKey = buildCacheKey(request);
    const cached = justificationPromiseCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const pendingPromise = apiUtil
        .postData<InsightActionJustificationResponse>(
            `${BASE_URL}/action-justification`,
            request
        )
        .then((response) => response.justification?.trim() ?? "");

    justificationPromiseCache.set(cacheKey, pendingPromise);
    return pendingPromise;
}
